import type { BlockNode } from '../types';

export interface SectionPreset {
  id: string;
  title: string;
  category: 'canonical' | 'headers' | 'heroes' | 'features' | 'courses' | 'faculty' | 'results' | 'store' | 'lead_forms' | 'faq_notices' | 'footers' | 'cta';
  categoryLabel: string;
  description: string;
  previewColor: string;
  isCanonical?: boolean;
  canonicalOrder?: number;
  blocks: BlockNode[];
}

export const SECTION_PRESETS: SectionPreset[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // THE 8 CANONICAL GENERIC MODULAR SECTIONS (Requirement R3)
  // ───────────────────────────────────────────────────────────────────────────

  // 1. HERO (Section + HeroSplit)
  {
    id: 'canonical_hero',
    title: '1. Hero Section (Split Hero)',
    category: 'heroes',
    categoryLabel: 'Hero Showcase',
    description: 'High-converting split hero with national rank badge, compelling copy, brochure download, and student photo.',
    previewColor: 'from-indigo-600 to-purple-600',
    isCanonical: true,
    canonicalOrder: 1,
    blocks: [
      {
        id: 'hero-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'hero' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
        children: [
          {
            id: 'hero-split-1',
            type: 'HeroSplit',
            category: 'layout',
            props: {
              headline: 'Empower Your Dreams to Crack JEE & NEET',
              subheadline: 'Learn from top Ex-IITian faculty with proven structured pedagogy, daily practice problems, and personal mentoring.',
              ctaText: 'Explore Entrance Batches →',
              ctaLink: '#courses',
              imageURL: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=900&auto=format&fit=crop&q=80',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 2. FEATURES (Section + Heading + Subtitle + 4 feature cards)
  {
    id: 'canonical_features',
    title: '2. Features Section (Why Choose Us)',
    category: 'features',
    categoryLabel: 'Features & Pedagogy',
    description: '4-pillar institutional advantages: structured pedagogy, master faculty, simulated test series, and 1-on-1 mentorship.',
    previewColor: 'from-blue-600 to-cyan-600',
    isCanonical: true,
    canonicalOrder: 2,
    blocks: [
      {
        id: 'features-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'features' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'features-header-1',
            type: 'Heading',
            category: 'typography',
            props: { text: 'Why Choose Helios Premier Academy', level: 'h2', align: 'center' },
            styles: { base: { margin: '0 0 8px 0', textAlign: 'center' } },
          },
          {
            id: 'features-sub-1',
            type: 'Paragraph',
            category: 'typography',
            props: { text: 'Engineered for national rank achievement through rigorous academic systems and personalized guidance.' },
            styles: { base: { margin: '0 0 32px 0', textAlign: 'center', color: '#64748b' } },
          },
          {
            id: 'features-grid-row1',
            type: 'Grid2Col',
            category: 'layout',
            props: { gap: '20px' },
            styles: { base: { marginBottom: '20px' } },
            children: [
              {
                id: 'feat-card-1',
                type: 'Container',
                category: 'layout',
                props: {},
                styles: { base: { padding: '24px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' } },
                children: [
                  {
                    id: 'feat-card-1-title',
                    type: 'Heading',
                    category: 'typography',
                    props: { text: '📚 Structured Pedagogy', level: 'h3', align: 'left' },
                    styles: { base: { margin: '0 0 8px 0' } },
                  },
                  {
                    id: 'feat-card-1-desc',
                    type: 'Paragraph',
                    category: 'typography',
                    props: { text: 'Concept-first syllabus architecture with graded daily practice problems (DPPs) tailored to examination patterns.' },
                    styles: { base: { color: '#64748b', fontSize: '13px' } },
                  },
                ],
              },
              {
                id: 'feat-card-2',
                type: 'Container',
                category: 'layout',
                props: {},
                styles: { base: { padding: '24px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' } },
                children: [
                  {
                    id: 'feat-card-2-title',
                    type: 'Heading',
                    category: 'typography',
                    props: { text: '👨‍🏫 Ex-IITian & Doctor Faculty', level: 'h3', align: 'left' },
                    styles: { base: { margin: '0 0 8px 0' } },
                  },
                  {
                    id: 'feat-card-2-desc',
                    type: 'Paragraph',
                    category: 'typography',
                    props: { text: 'Direct mentorship from seasoned educators with decades of proven success in coaching All-India top 100 rankers.' },
                    styles: { base: { color: '#64748b', fontSize: '13px' } },
                  },
                ],
              },
            ],
          },
          {
            id: 'features-grid-row2',
            type: 'Grid2Col',
            category: 'layout',
            props: { gap: '20px' },
            styles: { base: {} },
            children: [
              {
                id: 'feat-card-3',
                type: 'Container',
                category: 'layout',
                props: {},
                styles: { base: { padding: '24px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' } },
                children: [
                  {
                    id: 'feat-card-3-title',
                    type: 'Heading',
                    category: 'typography',
                    props: { text: '🎯 All-India Test Series (AITS)', level: 'h3', align: 'left' },
                    styles: { base: { margin: '0 0 8px 0' } },
                  },
                  {
                    id: 'feat-card-3-desc',
                    type: 'Paragraph',
                    category: 'typography',
                    props: { text: 'National simulated computer-based testing with AI performance analytics and speed-accuracy matrices.' },
                    styles: { base: { color: '#64748b', fontSize: '13px' } },
                  },
                ],
              },
              {
                id: 'feat-card-4',
                type: 'Container',
                category: 'layout',
                props: {},
                styles: { base: { padding: '24px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' } },
                children: [
                  {
                    id: 'feat-card-4-title',
                    type: 'Heading',
                    category: 'typography',
                    props: { text: '💡 1-on-1 Doubt Resolution', level: 'h3', align: 'left' },
                    styles: { base: { margin: '0 0 8px 0' } },
                  },
                  {
                    id: 'feat-card-4-desc',
                    type: 'Paragraph',
                    category: 'typography',
                    props: { text: 'Daily doubt clinics and individualized academic tracking so no student is left behind on core foundational concepts.' },
                    styles: { base: { color: '#64748b', fontSize: '13px' } },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // 3. COURSES (Section + CoachCourseCatalogBlock)
  {
    id: 'canonical_courses',
    title: '3. Courses Section (Entrance Batches)',
    category: 'courses',
    categoryLabel: 'Courses & Batches',
    description: 'Interactive course cards for JEE, NEET, and Foundation batches with duration, ratings, and syllabus enrollment buttons.',
    previewColor: 'from-emerald-600 to-teal-600',
    isCanonical: true,
    canonicalOrder: 3,
    blocks: [
      {
        id: 'courses-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'courses' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
        children: [
          {
            id: 'course-catalog-1',
            type: 'CoachCourseCatalogBlock',
            category: 'erp_binding',
            props: {
              title: 'Upcoming Classroom & Hybrid Batches 2026–27',
              text: 'Upcoming Entrance Batches',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 4. TUTORS / FACULTY (Section + FacultyShowcase)
  {
    id: 'canonical_faculty',
    title: '4. Tutors & Faculty Showcase',
    category: 'faculty',
    categoryLabel: 'Master Educators',
    description: 'Distinguished faculty profiles with subject specialties, academic qualifications, and mentoring experience.',
    previewColor: 'from-violet-600 to-indigo-700',
    isCanonical: true,
    canonicalOrder: 4,
    blocks: [
      {
        id: 'faculty-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'faculty' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'faculty-showcase-1',
            type: 'FacultyShowcase',
            category: 'academics',
            props: {
              headline: 'Meet Our Master Educators',
              subheadline: 'Learn from distinguished educators, ex-IITians, and subject masters dedicated to student rank achievement.',
              faculty1_name: 'Prof. Arvind Trivedi',
              faculty1_subject: 'Advanced Physics',
              faculty1_qualification: 'B.Tech IIT Kanpur (AIR 38)',
              faculty1_exp: '18+ Years Exp',
              faculty1_bio: 'Mentor to 120+ Top 100 AIR rankers. Renowned for intuitive mechanics and electrodynamics problem solving.',
              faculty2_name: 'Dr. Meenakshi Sunder',
              faculty2_subject: 'Organic & Inorganic Chemistry',
              faculty2_qualification: 'Ph.D. IISc Bangalore',
              faculty2_exp: '14+ Years Exp',
              faculty2_bio: 'Specializes in reaction mechanism mastery and mnemonics that make complex transformations effortless.',
              faculty3_name: 'Er. Rajesh Nair',
              faculty3_subject: 'Pure & Applied Mathematics',
              faculty3_qualification: 'M.Tech IIT Kharagpur',
              faculty3_exp: '12+ Years Exp',
              faculty3_bio: 'Author of bestselling entrance problem sets in calculus, coordinate geometry, and combinatorial analysis.',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 5. TESTIMONIALS (Section + Testimonials)
  {
    id: 'canonical_testimonials',
    title: '5. Testimonials & Student Reviews',
    category: 'results',
    categoryLabel: 'Results & Achievements',
    description: '3-card authentic review section with star ratings, student admission outcomes, and parent feedback.',
    previewColor: 'from-amber-500 to-orange-600',
    isCanonical: true,
    canonicalOrder: 5,
    blocks: [
      {
        id: 'test-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'testimonials' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
        children: [
          {
            id: 'testimonials-1',
            type: 'Testimonials',
            category: 'academics',
            props: {
              headline: 'What Our Students and Parents Say',
              subheadline: 'Read real journeys from students who transformed their potential into national success.',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 6. FAQ (Section + AccordionFAQ)
  {
    id: 'canonical_faq',
    title: '6. FAQ (Admissions & Curriculum)',
    category: 'faq_notices',
    categoryLabel: 'FAQ & Campus Notices',
    description: 'Clean expandable FAQ accordion addressing admission criteria, fee refunds, hostel facilities, and scholarships.',
    previewColor: 'from-sky-500 to-blue-600',
    isCanonical: true,
    canonicalOrder: 6,
    blocks: [
      {
        id: 'faq-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'faq' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'faq-acc-1',
            type: 'AccordionFAQ',
            category: 'academics',
            props: {
              headline: 'Frequently Asked Questions',
              subheadline: 'Everything you need to know about batch timings, entrance scholarships, and study materials.',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 7. CONTACT (Section + ConversionFormBlock / campus info)
  {
    id: 'canonical_contact',
    title: '7. Contact & Campus Admissions',
    category: 'lead_forms',
    categoryLabel: 'Lead Generation & Forms',
    description: 'Campus locations, counselor hotline, and instant consultation booking form with CRM integration.',
    previewColor: 'from-rose-500 to-red-600',
    isCanonical: true,
    canonicalOrder: 7,
    blocks: [
      {
        id: 'contact-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'contact' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
        children: [
          {
            id: 'form-block-1',
            type: 'ConversionFormBlock',
            category: 'conversion',
            props: {
              formTitle: 'Request Free Academic Counseling & Demo Class',
              submitButtonLabel: 'Book Free Counseling Session →',
              successMessage: 'Thank you! Our academic director will contact you within 2 business hours.',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // 8. CALL-TO-ACTION / CTA (Section + CallToActionBanner)
  {
    id: 'canonical_cta',
    title: '8. Call to Action (Scholarship Banner)',
    category: 'cta',
    categoryLabel: 'Promotions & CTAs',
    description: 'High-impact scholarship urgency banner with bold headline, fee waiver details, and direct registration action.',
    previewColor: 'from-purple-600 to-pink-600',
    isCanonical: true,
    canonicalOrder: 8,
    blocks: [
      {
        id: 'cta-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'cta' },
        styles: { base: { padding: '48px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'cta-banner-1',
            type: 'CallToActionBanner',
            category: 'conversion',
            props: {
              headline: 'Ready to Begin Your Journey to an All-India Rank?',
              subheadline: 'Register today for our diagnostic scholarship test and claim up to 90% fee waiver on all 2026/2027 entrance batches.',
              ctaText: 'Apply For Scholarship Test →',
              ctaLink: '/home#enquiry',
              variant: 'primary',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // ADDITIONAL SPECIALIZED PRESETS
  // ───────────────────────────────────────────────────────────────────────────

  // Top Navigation Bar
  {
    id: 'header_academy_standard',
    title: 'Academy Top Navigation Bar',
    category: 'headers',
    categoryLabel: 'Headers & Navigation',
    description: 'Clean sticky navigation bar with academy logo, menu links, phone hotline, and Student Login CTA.',
    previewColor: 'from-blue-600 to-indigo-600',
    blocks: [
      {
        id: 'header-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'site-header' },
        styles: { base: { padding: '16px 24px', backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' } },
        children: [
          {
            id: 'header-grid-1',
            type: 'Grid2Col',
            category: 'layout',
            props: { gap: '16px' },
            styles: { base: { alignItems: 'center' } },
            children: [
              {
                id: 'header-brand-1',
                type: 'Heading',
                category: 'typography',
                props: { text: '⚡ Helios Premier Academy', level: 'h3', align: 'left' },
                styles: { base: { margin: '0px' } },
              },
              {
                id: 'header-cta-1',
                type: 'Button',
                category: 'typography',
                props: {
                  text: 'Admissions Enquiry ↗',
                  link: '#enquiry',
                  variant: 'primary',
                  size: 'sm',
                  align: 'right',
                },
                styles: { base: {} },
              },
            ],
          },
        ],
      },
    ],
  },

  // Legacy & Rank Counter
  {
    id: 'stats_proven_results',
    title: '4-Metric Legacy & Rank Counter',
    category: 'results',
    categoryLabel: 'Results & Achievements',
    description: 'High-impact 4-metric achievements highlighting selections, IITians, doctors, and percentile records.',
    previewColor: 'from-amber-500 to-orange-600',
    blocks: [
      {
        id: 'stats-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'achievements' },
        styles: { base: { padding: '48px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'stats-counter-1',
            type: 'StatsCounter',
            category: 'conversion',
            props: {
              label: 'Our National Legacy',
              stat1_value: '1,250+',
              stat1_label: 'IIT & AIIMS Selections',
              stat2_value: '99.98%',
              stat2_label: 'Highest JEE Percentile',
              stat3_value: '18+ Yrs',
              stat3_label: 'Pedagogical Excellence',
              stat4_value: '45+',
              stat4_label: 'Master Faculty Mentors',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // All-India Rankers Wall of Fame
  {
    id: 'toppers_wall_of_fame',
    title: 'All-India Rankers Wall of Fame',
    category: 'results',
    categoryLabel: 'Results & Achievements',
    description: 'Trophy showcase honoring top rank holders with exam ranks, scores, photos, and verified testimonials.',
    previewColor: 'from-yellow-500 to-amber-600',
    blocks: [
      {
        id: 'toppers-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'toppers' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'toppers-showcase-1',
            type: 'ToppersShowcase',
            category: 'academics',
            props: {
              headline: 'Celebrating Our All-India Rankers',
              subheadline: 'Dedication, relentless practice, and personalized mentoring delivered these stellar national ranks.',
              badgeText: 'National Hall of Fame',
            },
            styles: { base: {} },
            children: [
              {
                id: 'topper-1',
                type: 'TopperCard',
                category: 'academics',
                props: {
                  studentName: 'Aarav Sharma',
                  examName: 'JEE Advanced 2025',
                  rank: 'AIR 42',
                  score: 'Score: 312/360',
                  photoUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
                  testimonial: 'The rigorous test series and doubt-clearing sessions were the cornerstone of my rank.',
                  batchYear: 'Two-Year Classroom Batch',
                },
                styles: { base: {} },
              },
              {
                id: 'topper-2',
                type: 'TopperCard',
                category: 'academics',
                props: {
                  studentName: 'Sneha Patel',
                  examName: 'NEET UG 2025',
                  rank: 'AIR 88',
                  score: 'Score: 710/720',
                  photoUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&auto=format&fit=crop&q=80',
                  testimonial: 'NCERT line-by-line revision maps and simulated grand tests gave me speed and accuracy.',
                  batchYear: 'Target Medical Batch',
                },
                styles: { base: {} },
              },
              {
                id: 'topper-3',
                type: 'TopperCard',
                category: 'academics',
                props: {
                  studentName: 'Devansh Kulkarni',
                  examName: 'JEE Main 2025',
                  rank: '99.98%ile',
                  score: 'Maths: 100/100',
                  photoUrl: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&auto=format&fit=crop&q=80',
                  testimonial: 'Helios mentors solved complex problem variants that made the actual exam feel effortless.',
                  batchYear: 'Super 30 Intensive',
                },
                styles: { base: {} },
              },
            ],
          },
        ],
      },
    ],
  },

  // Admission Plans & Fee Tiers
  {
    id: 'pricing_admission_packages',
    title: 'Admission Plans & Fee Tiers',
    category: 'courses',
    categoryLabel: 'Courses & Batches',
    description: '3-tier transparent fee packages with feature checkmarks, scholarship eligibility, and enrollment buttons.',
    previewColor: 'from-blue-500 to-indigo-600',
    blocks: [
      {
        id: 'pricing-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'admissions-fees' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#ffffff' } },
        children: [
          {
            id: 'pricing-table-1',
            type: 'PricingTable',
            category: 'commerce',
            props: {
              headline: 'Transparent Admission & Fee Packages',
              subheadline: 'Choose the coaching tier tailored to your target examination year and preparation intensity.',
            },
            styles: { base: {} },
          },
        ],
      },
    ],
  },

  // Digital Study Store
  {
    id: 'store_digital_materials',
    title: 'Digital Study Material & Mock Tests',
    category: 'store',
    categoryLabel: 'Digital Store & Products',
    description: 'Digital store section with downloadable formula cheat-sheets, mock test series PDFs, and instant checkout.',
    previewColor: 'from-purple-600 to-pink-600',
    blocks: [
      {
        id: 'store-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'study-store' },
        styles: { base: { padding: '56px 24px', backgroundColor: '#f8fafc' } },
        children: [
          {
            id: 'digital-list-1',
            type: 'DigitalProductList',
            category: 'commerce',
            props: { sectionTitle: 'Official Revision Sheets & Mock Test Packs' },
            styles: { base: {} },
            children: [
              {
                id: 'prod-1',
                type: 'DigitalProductCard',
                category: 'commerce',
                props: {
                  title: 'Physics Formula Booklet (JEE & NEET)',
                  description: 'All 32 chapters summarized with high-yield derivations, shortcut formulas, and previous 10-year question tags.',
                  price: 299,
                  currency: 'INR',
                  fileType: 'PDF Document',
                  badge: 'Best Seller',
                  buttonText: 'Instant PDF Download',
                },
                styles: { base: {} },
              },
              {
                id: 'prod-2',
                type: 'DigitalProductCard',
                category: 'commerce',
                props: {
                  title: 'Organic Chemistry Reaction Mindmaps',
                  description: 'Master all named reactions, reagents, and mechanisms in color-coded visual charts for rapid recall.',
                  price: 399,
                  currency: 'INR',
                  fileType: 'PDF + High-Res Maps',
                  badge: 'Top Rated',
                  buttonText: 'Instant PDF Download',
                },
                styles: { base: {} },
              },
              {
                id: 'prod-3',
                type: 'DigitalProductCard',
                category: 'commerce',
                props: {
                  title: 'Full-Syllabus Simulated Mock Test Series',
                  description: '15 Full-length exam papers with detailed step-by-step video solution keys and percentile estimators.',
                  price: 899,
                  currency: 'INR',
                  fileType: '15 Test Bundle',
                  badge: 'Exam Ready',
                  buttonText: 'Enroll in Test Series',
                },
                styles: { base: {} },
              },
            ],
          },
        ],
      },
    ],
  },

  // Complete Academy Footer
  {
    id: 'footer_complete_academy',
    title: 'Complete Academy Footer & Legal Links',
    category: 'footers',
    categoryLabel: 'Footers & Legal',
    description: 'Professional 4-column footer with campus address, contact phone/email, quick links, and legal compliance pages.',
    previewColor: 'from-slate-700 to-slate-900',
    blocks: [
      {
        id: 'footer-sec-1',
        type: 'Section',
        category: 'layout',
        props: { sectionId: 'site-footer' },
        styles: { base: { padding: '48px 24px 24px', backgroundColor: '#0f172a', color: '#f8fafc' } },
        children: [
          {
            id: 'footer-brand-1',
            type: 'Heading',
            category: 'typography',
            props: { text: '⚡ Helios Premier Coaching Academy', level: 'h3', align: 'left' },
            styles: { base: { color: '#ffffff', marginBottom: '8px' } },
          },
          {
            id: 'footer-desc-1',
            type: 'Paragraph',
            category: 'typography',
            props: {
              text: 'Nurturing India’s finest engineers and doctors with world-class faculty, scientific study modules, and relentless mentorship.',
            },
            styles: { base: { color: '#94a3b8', fontSize: '13px', maxWidth: '640px' } },
          },
          {
            id: 'footer-divider-1',
            type: 'Divider',
            category: 'layout',
            props: { thickness: '1px', style: 'solid' },
            styles: { base: { borderColor: '#334155', margin: '24px 0 16px' } },
          },
          {
            id: 'footer-legal-bar-1',
            type: 'Paragraph',
            category: 'typography',
            props: {
              text: '© 2026 Helios Premier Academy. All rights reserved. • Terms & Conditions • Privacy Policy • Cookie Policy',
            },
            styles: { base: { color: '#64748b', fontSize: '11px', textAlign: 'center' } },
          },
        ],
      },
    ],
  },
];
